<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-transport-plan-progress">
    <nx-report-header title="计划完成进度条">
      <div class="kb-header-actions">
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="作业日期">
        <el-date-picker
          v-model="queryDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="选择日期"
          style="width: 150px"
        />
      </el-form-item>
      <el-form-item label="业务类型">
        <el-select v-model="businessType" clearable placeholder="全部" style="width: 130px">
          <el-option label="铁运入库" :value="1" />
          <el-option label="汽运出库" :value="2" />
          <el-option label="汽运入库" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="loadData">查询</el-button>
      </el-form-item>
    </el-form>

    <el-alert
      v-if="loadError"
      class="kb-error-alert"
      type="error"
      :title="loadError"
      show-icon
      :closable="false"
    />

    <div class="kb-kpi-row">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">日计划数</div>
        <div class="kb-kpi-value">{{ summary.totalCount }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">红色预警数</div>
        <div class="kb-kpi-value is-red">{{ summary.redCount }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">黄色预警数</div>
        <div class="kb-kpi-value is-yellow">{{ summary.yellowCount }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">绿色正常数</div>
        <div class="kb-kpi-value is-green">{{ summary.greenCount }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">吨位加权完成率</div>
        <div class="kb-kpi-value">{{ fmtPct(summary.weightedRatePercent) }}</div>
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-head">
        <span>计划进度条</span>
      </div>
      <div v-if="items.length" class="progress-list">
        <div v-for="row in items" :key="row.id || row.planNo" :class="['plan-row', 'is-' + warningClass(row)]">
          <div class="plan-main">
            <div class="plan-name" :title="row.planName || row.planNo">
              {{ row.planName || row.planNo || '未命名计划' }}
            </div>
            <div class="plan-meta" :title="planMeta(row)">
              {{ planMeta(row) }}
            </div>
          </div>
          <div class="plan-progress">
            <div class="track">
              <i :class="'bar is-' + warningClass(row)" :style="{ width: barWidth(row) }" />
            </div>
            <div class="ton-line">
              计划 {{ fmtTon(row.planTotalWeight) }} 吨 / 执行 {{ fmtTon(row.planExecuteWeight) }} 吨
            </div>
          </div>
          <div class="plan-state">
            <div :class="['percent', 'is-' + warningClass(row)]">{{ fmtPct(row.completionRatePercent) }}</div>
            <el-tag :type="tagType(row)" size="small">{{ warningText(row) }}</el-tag>
          </div>
        </div>
      </div>
      <el-empty v-else-if="!loading" description="暂无计划完成进度数据" :image-size="80" />
    </div>

    <div class="kb-section">
      <div class="kb-section-head">
        <span>计划明细</span>
      </div>
      <el-table :data="items" border stripe size="small" max-height="460" style="width: 100%">
        <el-table-column prop="planNo" label="计划编号" min-width="130" show-overflow-tooltip />
        <el-table-column prop="planName" label="计划名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="businessTypeLabel" label="业务类型" width="100" />
        <el-table-column prop="custName" label="货主名称" min-width="110" show-overflow-tooltip />
        <el-table-column prop="cargoName" label="煤种名称" min-width="110" show-overflow-tooltip />
        <el-table-column label="计划区间" min-width="126">
          <template slot-scope="{ row }">
            <span class="date-range">{{ row.intervalStartStr || '—' }}<br />~ {{ row.intervalEndStr || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="planTotalWeight" label="计划数量（吨）" width="118" align="right" :formatter="fmtTonCol" />
        <el-table-column prop="planExecuteWeight" label="执行数量（吨）" width="118" align="right" :formatter="fmtTonCol" />
        <el-table-column label="完成率" min-width="148">
          <template slot-scope="{ row }">
            <span :class="['table-rate', 'is-' + warningClass(row)]">{{ fmtPct(row.completionRatePercent) }}</span>
            <div v-if="row.completionRatePercent != null" class="table-bar">
              <i :class="'is-' + warningClass(row)" :style="{ width: barWidth(row) }" />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="预警等级" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="tagType(row)" size="small">{{ warningText(row) }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchTransportPlanProgress } from './api'

export default {
  name: 'KanbanTransportPlanProgress',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      loadError: '',
      queryDate: moment().format('YYYY-MM-DD'),
      businessType: null,
      result: null
    }
  },
  computed: {
    summary() {
      return (
        (this.result && this.result.summary) || {
          totalCount: 0,
          redCount: 0,
          yellowCount: 0,
          greenCount: 0,
          weightedRatePercent: null
        }
      )
    },
    items() {
      return (this.result && this.result.items) || []
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      if (!this.queryDate) {
        this.queryDate = moment().format('YYYY-MM-DD')
      }
      this.loading = true
      this.loadError = ''
      try {
        this.result = await fetchTransportPlanProgress({
          queryDate: this.queryDate,
          businessType: this.businessType
        })
      } catch (e) {
        this.loadError = (e && e.message) || '加载计划完成进度失败'
        this.$message.error(this.loadError)
      } finally {
        this.loading = false
      }
    },
    warningClass(row) {
      const level = row && row.warningLevel
      if (['red', 'yellow', 'green', 'none'].indexOf(level) >= 0) return level
      const rate = row ? Number(row.completionRatePercent) : NaN
      if (!Number.isFinite(rate)) return 'none'
      if (rate < 60) return 'red'
      if (rate <= 80) return 'yellow'
      return 'green'
    },
    warningText(row) {
      if (row && row.warningLabel) return row.warningLabel
      const level = this.warningClass(row)
      if (level === 'red') return '红色预警'
      if (level === 'yellow') return '黄色预警'
      if (level === 'green') return '绿色标识'
      return '无计划量'
    },
    tagType(row) {
      const level = this.warningClass(row)
      if (level === 'red') return 'danger'
      if (level === 'yellow') return 'warning'
      if (level === 'green') return 'success'
      return 'info'
    },
    barWidth(row) {
      const rate = row ? Number(row.completionRatePercent) : NaN
      if (!Number.isFinite(rate)) return '0%'
      return Math.min(100, Math.max(0, rate)) + '%'
    },
    planMeta(row) {
      return [row.businessTypeLabel || '未知业务', row.custName || '未知货主', row.cargoName || '未知煤种']
        .filter(Boolean)
        .join(' · ')
    },
    fmtPct(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(1) + '%'
    },
    fmtTon(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    fmtTonCol(row, col, val) {
      return this.fmtTon(val != null ? val : row[col.property])
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
